import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { uniq } from 'lodash';
import { orderBy } from 'lodash/fp';

import WhenSalonFlag from 'components/core/WhenSalonFlag.component';
import Button from 'components/form/Button.component';
import KnobInput from 'components/form/KnobInput.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import FlareStar from 'components/generic/flare/FlareStar.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import WidgetTitle from 'components/widget/WidgetTitle.component';
import LINK from 'constants/link.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import useResettingState from 'hooks/useResettingState.hook';
import useResettingTip from 'hooks/useResettingTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import elementRect from 'utils/dom/elementRect.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import C from 'utils/values/constant.util';

import CN from './CategoryTimeOff.module.scss';

const CTAG = 'CategoryTimeOff';

const USE_ALERT_BANNER = false;

const MIN_ROW_COUNT = 2;

const DATA_PLACEHOLDER_CATEGORY = C([
  { name: 'Holiday', emoji: `${S.palm}` },
  { name: 'College', emoji: `${S.college}` },
  { name: 'Lunch', emoji: `${S.sandwich}` },
]);

const fromTimeOffCatToBookingsCount = (item) => asObject(item).numberOfBookings;

const CategoryTimeOff = ({
  mandatory,
  name: nameFromProps,
  onChange = alwaysNoop,
  options,
  selected: selectedFromProps,
  tag: tagFromProps,
}) => {
  const { t } = useTranslation();
  const name = fromAnyToTrimmed(nameFromProps);
  const tag = fromAnyToTrimmed(tagFromProps);

  const salonId = useCurrentSalonId();

  // NOTE: If there's a selected category, close/collapse the container
  const { val: isCollapsed, tip: toggleCollapse, yay: collapse } = useResettingTip(selectedFromProps);
  const [localSelected, setLocalSelected] = useResettingState(selectedFromProps);
  const selectedId = asObject(localSelected).id;

  const listRef = useRef(null);
  const { children: listChildren } = asObject(deRef(listRef));

  const [rows, setRows] = useState(0);
  const rowsCount = asArray(rows).length;
  const isExpandable = MIN_ROW_COUNT < rowsCount;

  const handleSelect = useCallback(
    (ev) => {
      const raw = !mandatory && selectedId === ev.name ? null : ev.raw;
      const { value } = asObject(call(`${CTAG}.onChange`, onChange, { name, tag, raw }));
      setLocalSelected(isMissing(value) ? raw : value);
      collapse();
    },
    [collapse, mandatory, name, onChange, selectedId, setLocalSelected, tag],
  );

  const categories = orderBy(fromTimeOffCatToBookingsCount, ['desc'], asArray(options));
  const categoriesCount = asArray(categories).length;

  useEffect(() => {
    if (!categoriesCount || !listChildren) return;
    const tops = [...listChildren].map((el) => elementRect({ el })).map((r) => r.top);
    // NOTE: the assumption is that every child on the same row will have the same value for top
    setRows(uniq(tops));
  }, [listChildren, categoriesCount]);

  const renderedTooltipPlan = (
    <div className={CN.tooltipText}>
      {S.star}
      {S.space}
      {t('scheduler.timeOff.upgradePlan')}
    </div>
  );

  return (
    <div data-bem="CategoryTimeOff" data-tag={tag} className={CN.component}>
      <WidgetTitle tag="CategoryTimeOff__title">{t('scheduler.timeOff.categoryTitle')}</WidgetTitle>

      <div
        data-bem="CategoryTimeOff__body"
        className={cn({
          [CN.toggleContainer]: true,
          [CN.collapsed]: isCollapsed,
        })}
      >
        <div data-bem="CategoryTimeOff__list" data-tag={tag} ref={listRef} className={CN.list}>
          {categories.map((c) => (
            <div
              data-bem="CategoryTimeOff__item"
              key={c.id}
              className={selectedId === c.id ? [CN.selectedCategory] : ''}
            >
              <KnobInput
                tag="CategoryTimeOff__item"
                key={c.id}
                name={c.id}
                value={c}
                selected={selectedId === c.id}
                onClick={handleSelect}
              >
                {c.name}
              </KnobInput>
            </div>
          ))}

          <WhenSalonFlag name={SF.timeOffCats}>
            <div className={CN.buttonContainer}>
              <Button variant="emphasis">
                <NavLink
                  to={linkTo({ pattern: LINK.hrTimeoff, params: { salonId }, fault: '#' })}
                  className={CN.internalLink}
                >
                  {t('scheduler.timeOff.addCategories')}
                </NavLink>
              </Button>
            </div>
          </WhenSalonFlag>

          <WhenSalonFlag disabled name={SF.timeOffCats}>
            {DATA_PLACEHOLDER_CATEGORY.map(({ emoji, name }, index) => (
              <div key={createNodeKey({ index })} className={CN.fakeCategory}>
                <Tooltip variant="timeOffCategory" content={renderedTooltipPlan}>
                  <div className={CN.content}>
                    <div className={CN.icon}>
                      <Icon variant="lockDisabled" width="lg" />
                    </div>
                    <Space>{name}</Space>
                    {emoji}
                  </div>
                </Tooltip>
              </div>
            ))}
          </WhenSalonFlag>
        </div>
      </div>

      {isExpandable && (
        <WhenSalonFlag name={SF.timeOffCats}>
          <Button tag="CategoryTimeOff__toggle" variant="slim" onClick={toggleCollapse}>
            <div className={CN.flex}>
              {isCollapsed ? <Icon variant="plus" /> : <Icon variant="minus" />}
              <span className={CN.buttonMore}>
                {isCollapsed ? t('scheduler.timeOff.viewAll', { categoriesCount }) : t('scheduler.timeOff.viewLess')}
              </span>
            </div>
          </Button>
        </WhenSalonFlag>
      )}

      <WhenSalonFlag disabled name={SF.timeOffCats}>
        <div className={CN.alertSpace}>
          {USE_ALERT_BANNER && (
            <AlertPanel tag="CategoryTimeOff__alert" variant="primary">
              <Icon variant="lock" width="lg" />
              <div className={CN.alertText}>
                {t('scheduler.timeOff.upgradePart1')}
                <NavLink to={linkTo({ pattern: LINK.hrSplash, params: { salonId }, fault: '#' })}>
                  <div className={CN.bannerLink}>{t('scheduler.timeOff.upgradePart2')}</div>
                </NavLink>
                {t('scheduler.timeOff.upgradePart3')}
              </div>
              <FlareStar bottom="35" right="-3" size="14" animation="pulse" variant="dark" />
            </AlertPanel>
          )}
        </div>
      </WhenSalonFlag>
    </div>
  );
};

export default CategoryTimeOff;
